import { Timestamp } from 'firebase/firestore';
import type {
  CompanyMemberDto,
  CurrentUserDto,
  UpdateCurrentUserPayload,
  UpdateUserPayload,
  UserDto,
} from '@mesterplan/api-client';
import type {
  CreateUserModel,
  EmployeeAddress,
  EmployeeSalary,
  EmployeeSkill,
  PayrollProviderId,
  UserMembershipTypeModel,
  UserModel,
} from '@mesterplan/shared-types';

/**
 * Bridge between the canonical Firestore-era `UserModel`/`CreateUserModel` and the
 * .NET `api.users.*` DTO/payload surface (PR #723 field-complete contract).
 *
 * ## Enum wire format — VERIFIED STRING, not integer
 * Unlike the quote bridge, `AssignmentTradeType` is annotated on the .NET enum with
 * `[JsonConverter(typeof(AssignmentTradeTypeJsonConverter))]`, which emits the exact
 * lowercase TS wire tokens (`"carpenter"`, `"electrician"`, …). The api-client TS type
 * declares this as the same string union, so trade needs **no** int↔token map — it
 * copies 1:1. Team is now a per-company record (`team`/`teamId`), not an enum.
 *
 * ## Identity vs id routing
 * The *self* profile (current user) is selected server-side from the token, so it is
 * fetched/written via `me`/`updateMe` keyed on **identity**, never on `isGuid`
 * (the Firebase auth uid is a push-id and would wrongly route to Firestore). The
 * *admin* by-id path uses {@link isGuid}: a Guid lives in the .NET API; a legacy
 * Firestore push-id falls back to Firestore.
 *
 * ## Timestamps
 * `UserDto`/`CurrentUserDto` carry `createdAt` (ISO string, on `UserDto` only) and no
 * `updatedAt`. The Firestore `UserModel` is `Timestamp`-typed, so the mappers
 * synthesize Timestamps, falling back to a shadow Firestore doc's values where present.
 */

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Guid gate for the admin by-id path. A member keyed by a Guid (RsConnect sub, which is
 * what `companies.listMembers` returns) lives in the .NET API; a legacy Firestore
 * push-id falls back to Firestore.
 */
export function isGuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && GUID_PATTERN.test(value);
}

function isoToTimestamp(iso: string | null | undefined): Timestamp | undefined {
  if (!iso) {
    return undefined;
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? undefined : Timestamp.fromDate(parsed);
}

/** Strip nulls from the DTO's nullable objects so they map onto the optional Firestore shape. */
function nullToUndefined<T>(value: T | null | undefined): T | undefined {
  return value ?? undefined;
}

/**
 * Project the wire salary onto the shared-types `EmployeeSalary`. Unlike the mobile
 * bridge, `overhead` is kept **structured** (`{ type, amount }`) — the shared-types
 * `EmployeeSalary.overhead` is an `EmployeeOverhead`, and the web admin form
 * (`UserFormDialog`) reads `salary.overhead.type` / `salary.overhead.amount`. The only
 * job here is to normalise the DTO's `string | null` / `… | null` fields down to the
 * `?: …` (undefined) optionals the shared-types shape declares — every field on the
 * shared-types `EmployeeSalary` (incl. `source`/`sourceProvider`) is carried through for
 * 1:1 parity with the wire DTO.
 */
function mapSalary(dto: CurrentUserDto['salary'] | UserDto['salary']): EmployeeSalary | undefined {
  if (!dto) {
    return undefined;
  }
  return {
    type: dto.type,
    amount: dto.amount,
    currency: dto.currency,
    effectiveDate: dto.effectiveDate ?? undefined,
    overhead: dto.overhead
      ? { type: dto.overhead.type, amount: dto.overhead.amount }
      : undefined,
    source: dto.source ?? undefined,
    sourceProvider: (dto.sourceProvider ?? undefined) as PayrollProviderId | undefined,
  };
}

/** Normalise each skill's nullable `verifiedAt`/`verifiedById` to the optional shape. */
function mapSkills(dto: ReadonlyArray<UserDto['skills'][number]> | null | undefined): EmployeeSkill[] {
  return (dto ?? []).map((skill) => ({
    id: skill.id,
    name: skill.name,
    trade: skill.trade,
    verifiedAt: skill.verifiedAt ?? undefined,
    verifiedById: skill.verifiedById ?? undefined,
  }));
}

/**
 * Map the .NET `CurrentUserDto` (self read) onto the canonical `UserModel`. A shadow
 * Firestore doc (if any) supplies `createdAt`/`updatedAt`, otherwise they are synthesized.
 */
export function mapCurrentUserDto(
  id: string,
  dto: CurrentUserDto,
  shadow?: Partial<UserModel>,
): UserModel {
  const now = Timestamp.now();
  return {
    ...shadow,
    id,
    name: dto.displayName,
    email: dto.email,
    phoneNumber: nullToUndefined(dto.phoneNumber),
    address: nullToUndefined(dto.address),
    profileImage: nullToUndefined(dto.profileImage),
    structuredAddress: nullToUndefined(dto.structuredAddress) as EmployeeAddress | undefined,
    salary: mapSalary(dto.salary),
    primaryTrade: nullToUndefined(dto.primaryTrade),
    teamId: nullToUndefined(dto.teamId),
    team: nullToUndefined(dto.team),
    skills: mapSkills(dto.skills),
    isActive: dto.isActive,
    createdAt: shadow?.createdAt ?? now,
    updatedAt: shadow?.updatedAt ?? shadow?.createdAt ?? now,
  } as UserModel;
}

/**
 * Map the .NET `UserDto` (admin by-id read) onto the canonical `UserModel`. `createdAt`
 * comes from the DTO; `updatedAt` is synthesized (the DTO carries no update timestamp).
 */
export function mapUserDto(dto: UserDto, shadow?: Partial<UserModel>): UserModel {
  const createdAt = isoToTimestamp(dto.createdAt) ?? shadow?.createdAt ?? Timestamp.now();
  return {
    ...shadow,
    id: dto.id,
    name: dto.displayName,
    email: dto.email,
    phoneNumber: nullToUndefined(dto.phoneNumber),
    address: nullToUndefined(dto.address),
    profileImage: nullToUndefined(dto.profileImage),
    structuredAddress: nullToUndefined(dto.structuredAddress) as EmployeeAddress | undefined,
    salary: mapSalary(dto.salary),
    primaryTrade: nullToUndefined(dto.primaryTrade),
    teamId: nullToUndefined(dto.teamId),
    team: nullToUndefined(dto.team),
    skills: mapSkills(dto.skills),
    isActive: dto.isActive,
    createdAt,
    updatedAt: shadow?.updatedAt ?? createdAt,
  } as UserModel;
}

/**
 * Map a `CompanyMemberDto` from `api.companies.listMembers` onto the canonical
 * `UserModel` used by the employee pickers.
 *
 * The members endpoint is intentionally lean — identity, membership and the non-sensitive
 * employment details. It deliberately does **not** expose `salary`, `primaryTrade`, `skills`
 * or `structuredAddress`, because the list is gated on the role-managed `users.view`
 * permission (held by ProjectManager/Administration, not just admins) and those fields are
 * sensitive (salary in particular). Consumers that need the full employee profile — including
 * salary for cost calculations — must read the admin-gated by-id user endpoint
 * (`api.users.get`, gated on `settings.manageMembers`) via `useFetchUser`.
 *
 * The membership's team is carried as `teamId` + `team` — the Users list renders it as a
 * column and filters and sorts on those. The legacy wire-slug shim this mapper used to carry
 * alongside them was dropped in PR7; nothing in `web/src` has read it since PR6.
 *
 * `createdAt`/`updatedAt` are synthesized from `joinedAt`; the members endpoint carries no
 * user-row timestamps. A shadow Firestore doc (legacy, when present) backfills any
 * not-yet-migrated fields.
 */
export function mapMemberDto(dto: CompanyMemberDto, shadow?: Partial<UserModel>): UserModel {
  const joinedAt = isoToTimestamp(dto.joinedAt) ?? shadow?.createdAt ?? Timestamp.now();
  return {
    ...shadow,
    id: dto.userId,
    name: dto.displayName,
    email: dto.email,
    teamId: nullToUndefined(dto.teamId),
    team: nullToUndefined(dto.team),
    companyMemberships: [{ uid: dto.userId, role: dto.role as UserMembershipTypeModel }],
    createdAt: shadow?.createdAt ?? joinedAt,
    updatedAt: shadow?.updatedAt ?? shadow?.createdAt ?? joinedAt,
  } as UserModel;
}

/** Self profile write: ProfileSettings edits display name + phone only. */
export function toUpdateMePayload(data: Partial<CreateUserModel>): UpdateCurrentUserPayload {
  const payload: UpdateCurrentUserPayload = {};
  if (data.name !== undefined) {
    payload.displayName = data.name;
  }
  if (data.phoneNumber !== undefined) {
    payload.phoneNumber = data.phoneNumber ?? null;
  }
  return payload;
}

/**
 * Admin by-id write: carries the full editable employee profile with whole-object
 * replace semantics. Email is intentionally omitted (RsConnect-managed; backend rejects).
 */
export function toUpdateUserPayload(data: Partial<CreateUserModel> & { isActive?: boolean }): UpdateUserPayload {
  const payload: UpdateUserPayload = {};
  if (data.name !== undefined) {
    payload.displayName = data.name;
  }
  if (data.phoneNumber !== undefined) {
    payload.phoneNumber = data.phoneNumber ?? null;
  }
  if (data.address !== undefined) {
    payload.address = data.address ?? null;
  }
  if (data.profileImage !== undefined) {
    payload.profileImage = data.profileImage ?? null;
  }
  if (data.structuredAddress !== undefined) {
    payload.structuredAddress = (data.structuredAddress ?? null) as UpdateUserPayload['structuredAddress'];
  }
  if (data.salary !== undefined) {
    payload.salary = (data.salary ?? null) as UpdateUserPayload['salary'];
  }
  if (data.primaryTrade !== undefined) {
    payload.primaryTrade = data.primaryTrade ?? null;
  }
  if (data.teamId !== undefined) {
    payload.teamId = data.teamId ?? null;
  }
  if (data.skills !== undefined) {
    payload.skills = (data.skills ?? null) as UpdateUserPayload['skills'];
  }
  if (data.isActive !== undefined) {
    payload.isActive = data.isActive;
  }
  return payload;
}
